'use client';

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  AlertTriangle,
  ArrowRight,
  BarChart3,
  CheckCircle2,
  ChevronDown,
  Clock,
  History,
  Info,
  Loader2,
  Plus,
  RefreshCw,
  ShieldAlert,
  Sparkles,
  Target,
  Wand2,
  Zap,
} from 'lucide-react';
import { Button } from '@/components/ui/forms/button';
import {
  Dialog,
  DialogContent,
} from '@/components/ui/layout/dialog';
import { DialogHeading } from '@/components/ui/design-system';
import { Skeleton } from '@/components/ui/feedback/skeleton';
import { Badge, type BadgeProps } from '@/components/ui/data-display/badge';
import { ScoreRing } from '@/components/ui/data-display/score-ring';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/forms/select';
import { AgentProgress } from '@/components/ai/AgentProgress';
import { idleAgentState } from '@/lib/agent-kit/useAgentRun';
import type { AgentGenerationStep, AgentRunState } from '@/lib/agent-kit/types';
import {
  resumeAnalyzeStepCopy,
  resumeAnalyzeStepOrder,
} from '@/lib/resume/agent/analyze/step-graph';
import type { ResumeAnalyzeStepKey } from '@/lib/resume/agent/analyze/types';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { usePaywallDialogStore } from '@/stores/usePaywallDialogStore';
import { useResumeBuilderStore } from '@/stores/resume-builder-store';
import type { ResumeAnalysisResult, ResumeAnalysisSuggestion } from '@/lib/resume/analysis';
import { normalizeResumeContent } from '@/lib/resume/normalize-content';
import { compareResumeContent, compareResumeField, type ResumeChangeItem } from '@/lib/resume/compare';
import { ResumeComparisonDialog } from '@/components/features/resume/ResumeComparisonDialog';
import type { ResumeContent, ResumeData } from '@/lib/resume/types';

interface ResumeAnalyzeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resumeId: string;
  resumeName: string;
}

/**
 * Analysis reports are now background `Report` rows (`report_type:
 * 'RESUME_ANALYSIS'`) rather than state that lives and dies with this dialog.
 * The dialog's job changed from "run the agent and hold the result" to "list
 * the reports, load one, and poll one while it generates." The agent itself —
 * and its step graph / progress checklist — is unchanged; only how this dialog
 * learns about its progress changed, from an NDJSON stream read inline to a
 * poll of the persisted `generation_steps` on the report row.
 */

type AnalysisReportGenerationStatus =
  | 'PENDING'
  | 'RUNNING'
  | 'PAUSED'
  | 'COMPLETED'
  | 'FAILED'
  | 'CANCELLED';

interface AnalysisReportOverallSummary {
  score: number;
  label: string;
  summary: string;
}

/** Row shape returned by `GET /api/resume-builder/{resumeId}/analysis-reports`. */
interface AnalysisReportSummary {
  id: string;
  name: string;
  generation_status: AnalysisReportGenerationStatus;
  generation_steps?: AgentGenerationStep<ResumeAnalyzeStepKey>[] | null;
  content: { overall: AnalysisReportOverallSummary } | null;
  created_at: string;
}

/** Row shape returned by `GET .../analysis-reports/{analysisReportId}`. */
interface AnalysisReportDetail {
  id: string;
  name: string;
  generation_status: AnalysisReportGenerationStatus;
  generation_steps?: AgentGenerationStep<ResumeAnalyzeStepKey>[] | null;
  generation_error?: string | null;
  content: ResumeAnalysisResult | null;
  created_at: string;
}

const IN_PROGRESS_GENERATION_STATUSES: ReadonlySet<AnalysisReportGenerationStatus> = new Set([
  'PENDING',
  'RUNNING',
  'PAUSED',
]);

function isReportInProgress(status: AnalysisReportGenerationStatus): boolean {
  return IN_PROGRESS_GENERATION_STATUSES.has(status);
}

function getScoreBadgeVariant(score: number): BadgeProps['variant'] {
  if (score >= 75) return 'success';
  if (score >= 50) return 'secondary';
  return 'destructive';
}

function getGenerationStatusVariant(status: AnalysisReportGenerationStatus): BadgeProps['variant'] {
  if (status === 'FAILED' || status === 'CANCELLED') return 'destructive';
  return 'secondary';
}

/**
 * `generation_steps` as persisted may be a partial list (e.g. only the steps
 * that have transitioned so far). The checklist always wants the full plan, so
 * missing steps fill in as PENDING — same convention `idleAgentState` uses.
 */
function stepsFromGenerationSteps(
  steps: AgentGenerationStep<ResumeAnalyzeStepKey>[] | null | undefined
): AgentGenerationStep<ResumeAnalyzeStepKey>[] {
  const byKey = new Map((steps ?? []).map((step) => [step.step_key, step]));
  return resumeAnalyzeStepOrder.map(
    (key) => byKey.get(key) ?? { step_key: key, status: 'PENDING' as const }
  );
}

function agentStatusForGeneration(
  status: AnalysisReportGenerationStatus
): AgentRunState<ResumeAnalyzeStepKey, unknown>['status'] {
  if (status === 'COMPLETED') return 'ready';
  if (status === 'FAILED' || status === 'CANCELLED') return 'error';
  return 'running';
}

/**
 * The route answers a paywalled request with this exact message before a
 * report is ever queued. Kept in step with
 * src/app/api/resume-builder/[reportId]/analysis-reports/route.ts.
 */
const SUBSCRIPTION_REQUIRED_ERROR = 'Subscription required';

/** Each step summarises itself; the dialog just surfaces that one line. */
function summarizeAnalyzeStep(
  step: AgentGenerationStep<ResumeAnalyzeStepKey>
): string | null {
  const note = step.output?.note;
  return typeof note === 'string' && note.trim() ? note : null;
}

function formatGeneratedAt(timestamp: string): string {
  const parsed = new Date(timestamp);
  if (Number.isNaN(parsed.getTime())) {
    return 'Just now';
  }

  return parsed.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function getPriorityVariant(priority: ResumeAnalysisSuggestion['priority']) {
  if (priority === 'high') return 'destructive';
  if (priority === 'medium') return 'secondary';
  return 'outline';
}

function getDeltaLabel(delta: number | null) {
  if (delta === null) return null;
  if (delta > 0) return `+${delta} points`;
  if (delta < 0) return `${delta} points`;
  return 'No score change';
}

function getRiskVariant(risk: ResumeAnalysisResult['overall']['boilerplateRisk']) {
  if (risk === 'low') return 'success';
  if (risk === 'medium') return 'secondary';
  return 'destructive';
}

function AnalysisLoadingState({ progress }: { progress: ReactNode }) {
  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-border bg-gradient-to-br from-background via-background to-primary/5 p-4 sm:p-6">
        <div className="grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-primary">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-title-sm">Deep analysis in progress</span>
            </div>
            {progress}
            <p className="flex items-start gap-1.5 text-caption text-muted-foreground">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              You can close this and come back — the analysis runs in the background and your
              results will be waiting.
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-1">
            <div className="rounded-xl border border-border bg-background/80 p-4">
              <p className="text-overline text-muted-foreground">
                Overall Score
              </p>
              <Skeleton className="mt-3 h-16 w-28" />
            </div>
            <div className="rounded-xl border border-border bg-background/80 p-4">
              <p className="text-overline text-muted-foreground">
                Top Priorities
              </p>
              <div className="mt-3 space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-5/6" />
                <Skeleton className="h-4 w-4/6" />
              </div>
            </div>
            <div className="rounded-xl border border-border bg-background/80 p-4">
              <p className="text-overline text-muted-foreground">
                Guided Fixes
              </p>
              <div className="mt-3 space-y-2">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="rounded-xl border border-border bg-background p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-2">
                <Skeleton className="h-5 w-40" />
                <Skeleton className="h-4 w-56" />
              </div>
              <Skeleton className="h-14 w-14 rounded-full" />
            </div>
            <div className="mt-4 space-y-2">
              <Skeleton className="h-3.5 w-full" />
              <Skeleton className="h-3.5 w-11/12" />
              <Skeleton className="h-3.5 w-4/5" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function buildApplyAllInstructions(analysis: ResumeAnalysisResult): string {
  const lines = analysis.suggestions.map((suggestion, index) => {
    return `${index + 1}. [${suggestion.priority.toUpperCase()}] ${suggestion.title} (${suggestion.targetLabel}): ${suggestion.instruction}`;
  });

  return [
    'Apply the following resume-analysis recommendations in one coordinated pass.',
    'Keep the candidate voice natural and professional.',
    'Do not invent facts, metrics, employers, titles, dates, or tools.',
    'Address each item where the existing resume content supports it.',
    '',
    'Recommendations:',
    ...lines,
  ].join('\n');
}

function ApplyAllLoadingState() {
  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-border bg-gradient-to-br from-background via-background to-primary/5 p-4 sm:p-6">
        <div className="flex items-start gap-3">
          <Loader2 className="mt-0.5 h-5 w-5 animate-spin text-primary" />
          <div className="space-y-2">
            <p className="text-title-md text-foreground">Applying all guided improvements</p>
            <p className="max-w-2xl text-body text-muted-foreground">
              Building a full improved draft in the background, then preparing a before/after
              comparison before anything is saved.
            </p>
          </div>
        </div>
        <div className="mt-4 grid gap-3 md:grid-cols-3">
          <div className="rounded-xl border border-border bg-background/80 p-4">
            <p className="text-overline text-muted-foreground">
              Step 1
            </p>
            <p className="mt-2 text-body text-foreground">Gathering every analysis recommendation</p>
          </div>
          <div className="rounded-xl border border-border bg-background/80 p-4">
            <p className="text-overline text-muted-foreground">
              Step 2
            </p>
            <p className="mt-2 text-body text-foreground">Generating one coordinated improved draft</p>
          </div>
          <div className="rounded-xl border border-border bg-background/80 p-4">
            <p className="text-overline text-muted-foreground">
              Step 3
            </p>
            <p className="mt-2 text-body text-foreground">Preparing a comparison before commit</p>
          </div>
        </div>
      </div>
      <div className="grid gap-3 lg:grid-cols-2">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="rounded-xl border border-border bg-background p-4">
            <Skeleton className="h-5 w-40" />
            <Skeleton className="mt-3 h-4 w-full" />
            <Skeleton className="mt-2 h-4 w-11/12" />
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** One row in the desktop history rail. */
function AnalysisHistoryRow({
  report,
  isSelected,
  disabled,
  onSelect,
}: {
  report: AnalysisReportSummary;
  isSelected: boolean;
  disabled: boolean;
  onSelect: () => void;
}) {
  const isCompleted = report.generation_status === 'COMPLETED' && report.content;
  const score = isCompleted ? report.content!.overall.score : null;

  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled}
      className={cn(
        'flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60',
        isSelected ? 'bg-primary/8' : 'hover:bg-muted/50'
      )}
    >
      {score !== null ? (
        <span
          className={cn(
            'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-label font-bold',
            score >= 75
              ? 'bg-emerald-500/10 text-emerald-700'
              : score >= 50
                ? 'bg-amber-500/10 text-amber-700'
                : 'bg-red-500/10 text-red-700'
          )}
        >
          {score}
        </span>
      ) : (
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted">
          <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
        </span>
      )}
      <div className="min-w-0 flex-1">
        {isCompleted ? (
          <p className="truncate text-label text-foreground">
            {report.content!.overall.label}
          </p>
        ) : (
          <p className="text-label text-muted-foreground">
            {report.generation_status === 'FAILED' ? 'Failed' : 'Analyzing...'}
          </p>
        )}
        <p className="text-[11px] text-muted-foreground">
          {formatGeneratedAt(report.created_at)}
        </p>
      </div>
    </button>
  );
}

export default function ResumeAnalyzeDialog({
  open,
  onOpenChange,
  resumeId,
  resumeName,
}: ResumeAnalyzeDialogProps) {
  const { toast } = useToast();
  const showPaywallDialog = usePaywallDialogStore((state) => state.showPaywallDialog);
  const {
    resume,
    isSaving,
    hasUnsavedChanges,
    saveResume,
    setResume,
    updateContent,
    updateEmployment,
    updateCustomSection,
  } = useResumeBuilderStore();

  const [analysis, setAnalysis] = useState<ResumeAnalysisResult | null>(null);
  const [previousOverallScore, setPreviousOverallScore] = useState<number | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [activeSuggestionId, setActiveSuggestionId] = useState<string | null>(null);
  const [keepDoingExpanded, setKeepDoingExpanded] = useState(false);
  const [expandedSectionKeys, setExpandedSectionKeys] = useState<Set<string>>(new Set());
  const [agentState, setAgentState] = useState<AgentRunState<ResumeAnalyzeStepKey, unknown>>(() =>
    idleAgentState<ResumeAnalyzeStepKey, unknown>(resumeAnalyzeStepOrder)
  );

  // Report history — the list on the left, which report is currently open, and
  // the two loading states that are independent of `isAnalyzing` (which only
  // covers "a report is generating", not "we're fetching the list/a report").
  const [analysisReports, setAnalysisReports] = useState<AnalysisReportSummary[]>([]);
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);
  const [isLoadingReports, setIsLoadingReports] = useState(false);
  const [isLoadingReportDetail, setIsLoadingReportDetail] = useState(false);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [isGeneratingApplyAllPreview, setIsGeneratingApplyAllPreview] = useState(false);
  const [applyAllPreviewError, setApplyAllPreviewError] = useState<string | null>(null);
  const [applyAllPreviewContent, setApplyAllPreviewContent] = useState<ResumeContent | null>(null);
  const [applyAllPreviewChanges, setApplyAllPreviewChanges] = useState<ResumeChangeItem[]>([]);
  const [isApplyingAllPreview, setIsApplyingAllPreview] = useState(false);
  /**
   * Analysis on its own only scores a resume — nothing to compare. Applying a
   * single suggestion does rewrite one block and save it, and until now did so
   * with no more feedback than a toast, which is the one place in this dialog a
   * before/after actually has something to show.
   */
  const [appliedSuggestionChanges, setAppliedSuggestionChanges] = useState<ResumeChangeItem[]>([]);
  const [appliedSuggestionTitle, setAppliedSuggestionTitle] = useState('');

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  const resetApplyAllPreview = useCallback(() => {
    setIsGeneratingApplyAllPreview(false);
    setApplyAllPreviewError(null);
    setApplyAllPreviewContent(null);
    setApplyAllPreviewChanges([]);
    setIsApplyingAllPreview(false);
    setAppliedSuggestionChanges([]);
    setAppliedSuggestionTitle('');
  }, []);

  useEffect(() => {
    stopPolling();
    setAnalysis(null);
    setPreviousOverallScore(null);
    setAnalysisError(null);
    setActiveSuggestionId(null);
    setAgentState(idleAgentState<ResumeAnalyzeStepKey, unknown>(resumeAnalyzeStepOrder));
    setAnalysisReports([]);
    setSelectedReportId(null);
    setIsAnalyzing(false);
    resetApplyAllPreview();
  }, [resetApplyAllPreview, resumeId, stopPolling]);

  useEffect(() => {
    if (open) return;
    stopPolling();
    resetApplyAllPreview();
  }, [open, resetApplyAllPreview, stopPolling]);

  // Belt-and-suspenders: the interval must not outlive the component either.
  useEffect(() => () => stopPolling(), [stopPolling]);

  /** Poll one report's generation status every 2s until it lands. */
  const startPolling = useCallback(
    (analysisReportId: string) => {
      stopPolling();
      pollIntervalRef.current = setInterval(async () => {
        try {
          const res = await fetch(
            `/api/resume-builder/${resumeId}/analysis-reports/${analysisReportId}`
          );
          if (!res.ok) {
            const payload = (await res.json().catch(() => null)) as { error?: string } | null;
            throw new Error(payload?.error || 'Failed to check analysis progress');
          }
          const data = (await res.json()) as { report?: AnalysisReportDetail };
          const report = data.report;
          if (!report) return;

          const steps = stepsFromGenerationSteps(report.generation_steps);
          setAgentState({
            status: agentStatusForGeneration(report.generation_status),
            steps,
            currentStep: steps.find((step) => step.status === 'RUNNING')?.step_key ?? null,
            result: null,
            trace: null,
            error: report.generation_status === 'FAILED' ? report.generation_error ?? null : null,
          });

          setAnalysisReports((prev) =>
            prev.map((item) =>
              item.id === report.id
                ? {
                    ...item,
                    generation_status: report.generation_status,
                    generation_steps: report.generation_steps,
                    content: report.content ? { overall: report.content.overall } : null,
                  }
                : item
            )
          );

          if (report.generation_status === 'COMPLETED') {
            stopPolling();
            setIsAnalyzing(false);
            if (report.content) {
              setAnalysis(report.content);
              setAnalysisError(null);
            }
          } else if (report.generation_status === 'FAILED' || report.generation_status === 'CANCELLED') {
            stopPolling();
            setIsAnalyzing(false);
            setAnalysisError(report.generation_error || 'Analysis failed');
          }
        } catch (pollError) {
          // A transient poll failure isn't worth tearing down the interval over
          // — the next tick tries again. Only a terminal status stops polling.
          console.error('Failed to poll analysis report status:', pollError);
        }
      }, 2000);
    },
    [resumeId, stopPolling]
  );

  /** Open one report from the history rail — resume polling it, or load it. */
  const handleSelectReport = useCallback(
    async (reportId: string, reportsList: AnalysisReportSummary[]) => {
      stopPolling();
      setSelectedReportId(reportId);
      setAnalysisError(null);

      const summary = reportsList.find((item) => item.id === reportId);

      if (summary && isReportInProgress(summary.generation_status)) {
        setAnalysis(null);
        setIsAnalyzing(true);
        setAgentState({
          status: 'running',
          steps: stepsFromGenerationSteps(summary.generation_steps),
          currentStep: null,
          result: null,
          trace: null,
          error: null,
        });
        startPolling(reportId);
        return;
      }

      setIsAnalyzing(false);
      setIsLoadingReportDetail(true);
      try {
        const res = await fetch(`/api/resume-builder/${resumeId}/analysis-reports/${reportId}`);
        if (!res.ok) {
          const payload = (await res.json().catch(() => null)) as { error?: string } | null;
          throw new Error(payload?.error || 'Failed to load analysis report');
        }
        const data = (await res.json()) as { report?: AnalysisReportDetail };
        const report = data.report;
        if (!report) {
          throw new Error('Analysis report not found');
        }

        setAgentState({
          status: agentStatusForGeneration(report.generation_status),
          steps: stepsFromGenerationSteps(report.generation_steps),
          currentStep: null,
          result: null,
          trace: null,
          error: report.generation_status === 'FAILED' ? report.generation_error ?? null : null,
        });

        if (report.generation_status === 'FAILED' || report.generation_status === 'CANCELLED') {
          setAnalysis(null);
          setAnalysisError(report.generation_error || 'This analysis failed to generate.');
        } else if (report.content) {
          setAnalysis(report.content);
          setPreviousOverallScore(null);
        }
      } catch (loadError) {
        const message =
          loadError instanceof Error ? loadError.message : 'Failed to load analysis report';
        setAnalysisError(message);
        setAnalysis(null);
      } finally {
        setIsLoadingReportDetail(false);
      }
    },
    [resumeId, startPolling, stopPolling]
  );

  /** Fetch the report list on open, and land on the most recent report. */
  const loadReports = useCallback(async () => {
    if (!resumeId) return;
    setIsLoadingReports(true);
    setAnalysisError(null);
    try {
      const res = await fetch(`/api/resume-builder/${resumeId}/analysis-reports`);
      if (!res.ok) {
        const payload = (await res.json().catch(() => null)) as { error?: string } | null;
        throw new Error(payload?.error || 'Failed to load analysis history');
      }
      const data = (await res.json()) as { reports?: AnalysisReportSummary[] };
      const reports = data.reports ?? [];
      setAnalysisReports(reports);

      const latest = reports[0] ?? null;
      if (latest) {
        await handleSelectReport(latest.id, reports);
      } else {
        setSelectedReportId(null);
        setAnalysis(null);
        setAgentState(idleAgentState<ResumeAnalyzeStepKey, unknown>(resumeAnalyzeStepOrder));
      }
    } catch (loadError) {
      const message =
        loadError instanceof Error ? loadError.message : 'Failed to load analysis history';
      setAnalysisError(message);
    } finally {
      setIsLoadingReports(false);
    }
  }, [handleSelectReport, resumeId]);

  useEffect(() => {
    if (!open) return;
    void loadReports();
    // Only ever re-fetch when the dialog opens (or targets a different resume,
    // which already resets `resumeId`-keyed state above) — not on every
    // `loadReports` identity change, which would refetch mid-session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, resumeId]);

  /** Queue a brand-new background analysis report and start watching it. */
  const runAnalysis = useCallback(
    async (preservePreviousScore: boolean) => {
      if (!resumeId) return;

      if (preservePreviousScore && analysis) {
        setPreviousOverallScore(analysis.overall.score);
      } else if (!preservePreviousScore) {
        setPreviousOverallScore(null);
      }

      stopPolling();
      setAnalysisError(null);
      setIsAnalyzing(true);

      try {
        if (useResumeBuilderStore.getState().hasUnsavedChanges) {
          await saveResume();
          const latestState = useResumeBuilderStore.getState();
          if (latestState.hasUnsavedChanges) {
            throw new Error(latestState.error || 'Failed to save the latest resume changes.');
          }
        }

        const response = await fetch(`/api/resume-builder/${resumeId}/analysis-reports`, {
          method: 'POST',
        });

        const payload = (await response.json().catch(() => null)) as
          | { queued?: boolean; reportId?: string; error?: string }
          | null;

        if (!response.ok) {
          // The paywall gate answers before a report is queued, so its 402
          // reaches this as a normal error body rather than a stream failure.
          if (response.status === 402 || payload?.error === SUBSCRIPTION_REQUIRED_ERROR) {
            showPaywallDialog({ open: true, featureSlug: 'resume-analysis' });
            onOpenChange(false);
            return;
          }
          throw new Error(payload?.error || 'Failed to start analysis');
        }

        if (!payload?.reportId) {
          throw new Error('Analysis did not start');
        }

        const newEntry: AnalysisReportSummary = {
          id: payload.reportId,
          name: 'Resume Analysis',
          generation_status: 'PENDING',
          generation_steps: null,
          content: null,
          created_at: new Date().toISOString(),
        };

        setAnalysisReports((prev) => [newEntry, ...prev]);
        setSelectedReportId(payload.reportId);
        setAgentState({
          ...idleAgentState<ResumeAnalyzeStepKey, unknown>(resumeAnalyzeStepOrder),
          status: 'running',
        });
        startPolling(payload.reportId);
      } catch (runError) {
        const message =
          runError instanceof Error ? runError.message : 'Failed to analyze resume';
        setPreviousOverallScore(null);
        setAnalysisError(message);
        setIsAnalyzing(false);
        setAgentState((prev) => ({ ...prev, status: 'error', error: message }));
      }
    },
    [analysis, onOpenChange, resumeId, saveResume, showPaywallDialog, startPolling, stopPolling]
  );

  const handleApplySuggestion = useCallback(
    async (suggestion: ResumeAnalysisSuggestion) => {
      if (!resume || !suggestion.canAutoApply) return;

      let currentText = '';
      let field: 'summary' | 'responsibilities' | 'custom' | null = null;
      // Which resume section the rewrite lands in, for the before/after list.
      let comparisonSection = 'profile';
      let comparisonLabel = suggestion.targetLabel || suggestion.title;
      let context: { jobTitle?: string; company?: string } | undefined;
      let applyImprovedText: ((improvedText: string) => void) | null = null;
      const target = suggestion.target;

      switch (target.type) {
        case 'summary':
          currentText = resume.content.profile.summary || '';
          field = 'summary';
          comparisonSection = 'profile';
          comparisonLabel = 'Professional summary';
          applyImprovedText = (improvedText) => {
            updateContent({
              profile: {
                ...resume.content.profile,
                summary: improvedText,
              },
            });
          };
          break;
        case 'employment': {
          const employment = resume.content.employment.find(
            (item) => item.id === target.id
          );
          if (employment) {
            currentText = employment.responsibilities || '';
            field = 'responsibilities';
            comparisonSection = 'employment';
            comparisonLabel = `Updated responsibilities: ${employment.title || 'Role'} at ${employment.company || 'Company'}`;
            context = {
              jobTitle: employment.title,
              company: employment.company,
            };
            applyImprovedText = (improvedText) => {
              updateEmployment(employment.id, {
                responsibilities: improvedText,
              });
            };
          }
          break;
        }
        case 'customSection': {
          const section = resume.content.customSections.find(
            (item) => item.id === target.id
          );
          if (section) {
            currentText = section.content || '';
            field = 'custom';
            comparisonSection = 'customSections';
            comparisonLabel = `Updated custom section: ${section.title || 'Untitled section'}`;
            applyImprovedText = (improvedText) => {
              updateCustomSection(section.id, {
                content: improvedText,
              });
            };
          }
          break;
        }
        case 'manual':
        default:
          break;
      }

      if (!field || !applyImprovedText || !currentText.trim()) {
        toast({
          title: 'Cannot auto-apply this fix',
          description:
            'This suggestion needs a manual edit or the target block is currently empty.',
          variant: 'destructive',
        });
        return;
      }

      setActiveSuggestionId(suggestion.id);
      setAnalysisError(null);

      try {
        const response = await fetch('/api/resume-builder/improve', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            text: currentText,
            field,
            instructions: suggestion.instruction,
            context,
          }),
        });

        if (!response.ok) {
          const payload = (await response.json().catch(() => null)) as { error?: string } | null;
          throw new Error(payload?.error || 'Failed to apply improvement');
        }

        const payload = (await response.json()) as { improvedText?: string };
        const improvedText = payload.improvedText?.trim();
        if (!improvedText) {
          throw new Error('No improved text returned');
        }

        applyImprovedText(improvedText);
        await saveResume();

        const latestState = useResumeBuilderStore.getState();
        if (latestState.hasUnsavedChanges) {
          throw new Error(latestState.error || 'Failed to save the improved resume.');
        }

        setAppliedSuggestionTitle(suggestion.title);
        setAppliedSuggestionChanges(
          compareResumeField({
            section: comparisonSection,
            label: comparisonLabel,
            before: currentText,
            after: improvedText,
          })
        );

        toast({
          title: 'Improvement applied',
          description: 'The resume was updated. Re-scoring the latest version now.',
        });

        await runAnalysis(true);
      } catch (applyError) {
        const message =
          applyError instanceof Error
            ? applyError.message
            : 'Failed to apply the suggested improvement';
        toast({
          title: 'Apply improvement failed',
          description: message,
          variant: 'destructive',
        });
      } finally {
        setActiveSuggestionId(null);
      }
    },
    [
      resume,
      runAnalysis,
      saveResume,
      toast,
      updateContent,
      updateCustomSection,
      updateEmployment,
    ]
  );

  const handleGenerateApplyAllPreview = useCallback(async () => {
    if (!analysis || !resume || !resumeId) return;

    setApplyAllPreviewError(null);
    setAnalysisError(null);
    setIsGeneratingApplyAllPreview(true);

    try {
      if (useResumeBuilderStore.getState().hasUnsavedChanges) {
        await saveResume();
        const latestState = useResumeBuilderStore.getState();
        if (latestState.hasUnsavedChanges) {
          throw new Error(latestState.error || 'Failed to save the latest resume changes.');
        }
      }

      const latestResume = useResumeBuilderStore.getState().resume ?? resume;
      const response = await fetch(`/api/resume-builder/${resumeId}/improve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          instructions: buildApplyAllInstructions(analysis),
          persist: false,
        }),
      });

      if (!response.ok) {
        const payload = (await response.json().catch(() => null)) as { error?: string } | null;
        throw new Error(payload?.error || 'Failed to generate improved draft');
      }

      const payload = (await response.json()) as { content?: ResumeContent };
      if (!payload.content) {
        throw new Error('No improved draft was returned');
      }

      const beforeContent = normalizeResumeContent(latestResume.content);
      const afterContent = normalizeResumeContent(payload.content);
      setApplyAllPreviewContent(afterContent);
      setApplyAllPreviewChanges(compareResumeContent(beforeContent, afterContent));
    } catch (previewError) {
      const message =
        previewError instanceof Error ? previewError.message : 'Failed to generate improved draft';
      setApplyAllPreviewError(message);
      toast({
        title: 'Apply all failed',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setIsGeneratingApplyAllPreview(false);
    }
  }, [analysis, resume, resumeId, saveResume, toast]);

  const handleApplyAllPreview = useCallback(async () => {
    if (!applyAllPreviewContent || !resumeId) return;

    setIsApplyingAllPreview(true);
    setApplyAllPreviewError(null);
    setAnalysisError(null);

    try {
      const currentResume = useResumeBuilderStore.getState().resume ?? resume;
      if (!currentResume) {
        throw new Error('Resume is not loaded');
      }

      const response = await fetch(`/api/resume-builder/${resumeId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: currentResume.name,
          content: applyAllPreviewContent,
          settings: currentResume.settings,
        }),
      });

      const payload = (await response.json().catch(() => null)) as
        | { error?: string; resume?: ResumeData }
        | null;

      if (!response.ok) {
        throw new Error(payload?.error || 'Failed to save improved resume');
      }

      if (!payload?.resume) {
        throw new Error('Saved resume response was empty');
      }

      setResume(payload.resume);
      resetApplyAllPreview();

      toast({
        title: 'All improvements applied',
        description: 'Review accepted. Re-scoring the improved version now.',
      });

      await runAnalysis(true);
    } catch (applyError) {
      const message =
        applyError instanceof Error ? applyError.message : 'Failed to apply improved resume';
      setApplyAllPreviewError(message);
      toast({
        title: 'Save improved draft failed',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setIsApplyingAllPreview(false);
    }
  }, [applyAllPreviewContent, resetApplyAllPreview, resume, resumeId, runAnalysis, setResume, toast]);

  const scoreDelta = analysis ? analysis.overall.score - (previousOverallScore ?? analysis.overall.score) : null;
  const showScoreDelta = previousOverallScore !== null && analysis !== null;
  const autoApplySuggestionCount = analysis?.suggestions.filter((item) => item.canAutoApply).length ?? 0;
  const isBusy =
    isAnalyzing ||
    isSaving ||
    isLoadingReportDetail ||
    activeSuggestionId !== null ||
    isGeneratingApplyAllPreview ||
    isApplyingAllPreview;
  /**
   * Analysis is a background `Report` row (see `startPolling` above) — closing
   * the dialog only tears down the client-side poll, it never cancels the
   * generation job server-side. So unlike the other busy guards, the bottom
   * Close button must stay enabled while `isAnalyzing` is true; otherwise
   * users have no way to dismiss the dialog mid-analysis except the header
   * `X` / Escape, which undercuts the "safe to close" messaging above.
   */
  const isCloseBlocked =
    isSaving ||
    isLoadingReportDetail ||
    activeSuggestionId !== null ||
    isGeneratingApplyAllPreview ||
    isApplyingAllPreview;
  const isShowingApplyAllPreview = applyAllPreviewContent !== null;
  const showEmptyHistoryState =
    !isLoadingReports && analysisReports.length === 0 && !isAnalyzing;

  /**
   * The "Why" panel. Rebuilt from the persisted `generation_steps` rather than
   * a stream-only `trace.detail`: each step's own `output` already carries the
   * evidence counts, the validation verdict, and whether RECONCILE ran, so
   * nothing extra needs to be stored to reconstruct this once loaded from a
   * background report.
   */
  const analyzeDetail = useMemo<ReactNode>(() => {
    if (agentState.status !== 'ready') return null;

    const readStep = agentState.steps.find((step) => step.step_key === 'READ_RESUME');
    const validateStep = agentState.steps.find((step) => step.step_key === 'VALIDATE');
    const reconcileStep = agentState.steps.find((step) => step.step_key === 'RECONCILE');
    if (!readStep?.output && !validateStep?.output) return null;

    const quantified = Number(readStep?.output?.quantifiedClaims ?? 0);
    const asserted = Number(readStep?.output?.assertedClaims ?? 0);
    const targetRole =
      typeof readStep?.output?.targetRole === 'string' ? readStep.output.targetRole : null;
    const auditPassed = validateStep?.output?.verdict === 'pass';
    const validationNote =
      typeof validateStep?.output?.note === 'string' ? validateStep.output.note : null;
    const reconciled = reconcileStep?.status === 'COMPLETED';

    return (
      <div className="space-y-2 text-caption text-muted-foreground">
        <p>
          <span className="text-foreground">Evidence read:</span> {quantified} of{' '}
          {quantified + asserted} claims carry a number or a measurable outcome.
        </p>
        {targetRole && targetRole !== 'not stated' ? (
          <p>
            <span className="text-foreground">Scored against:</span> {targetRole}
          </p>
        ) : null}
        <p>
          <span className="text-foreground">Independent check:</span>{' '}
          {auditPassed
            ? 'a second model confirmed every score is supported by the evidence given for it.'
            : validationNote ||
              'a second model challenged some scores, and they were corrected.'}
        </p>
        {reconciled ? (
          <p>Scores and wording were adjusted after that check.</p>
        ) : null}
      </div>
    );
  }, [agentState.status, agentState.steps]);

  const analyzeProgress = (
    <AgentProgress<ResumeAnalyzeStepKey>
      steps={agentState.steps}
      copy={resumeAnalyzeStepCopy}
      status={agentState.status}
      error={agentState.error}
      doneTitle="How this score was produced"
      summarize={summarizeAnalyzeStep}
      detail={analyzeDetail}
      collapsible
      className="rounded-xl"
    />
  );

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85dvh] w-[calc(100vw-1rem)] max-w-6xl flex-col overflow-hidden p-0 sm:w-[calc(100vw-2rem)]">
        <DialogHeading
          title={<><BarChart3 className="mr-2 inline h-5 w-5" />Analyze Resume <Badge variant="premium" className="ml-1 align-middle text-label">Premium</Badge></>}
          description={<>Deep review for <span className="text-label text-foreground">{resumeName}</span>. This scores positioning, impact, polish, target fit, and boilerplate risk, then maps the best fixes to safe one-click improvements.</>}
          className="border-b-0"
        />

        <div className="flex flex-1 overflow-hidden">
          {/* Report history — desktop rail */}
          <aside className="hidden w-60 shrink-0 flex-col border-r border-border bg-muted/10 lg:flex">
            <div className="border-b border-border px-4 py-3">
              <p className="flex items-center gap-1.5 text-label text-foreground">
                <History className="h-3.5 w-3.5" />
                Analysis History
              </p>
            </div>
            <div className="px-3 pt-3">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => void runAnalysis(false)}
                disabled={isBusy}
              >
                <Plus className="mr-1.5 h-3.5 w-3.5" />
                New Analysis
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto px-3 py-3">
              {isLoadingReports ? (
                <div className="space-y-2">
                  <Skeleton className="h-16 w-full rounded-xl" />
                  <Skeleton className="h-16 w-full rounded-xl" />
                  <Skeleton className="h-16 w-full rounded-xl" />
                </div>
              ) : analysisReports.length === 0 ? (
                <p className="px-1 py-6 text-center text-caption text-muted-foreground">
                  No analyses yet.
                </p>
              ) : (
                <div className="space-y-2">
                  {analysisReports.map((report) => (
                    <AnalysisHistoryRow
                      key={report.id}
                      report={report}
                      isSelected={report.id === selectedReportId}
                      disabled={isLoadingReportDetail}
                      onSelect={() => void handleSelectReport(report.id, analysisReports)}
                    />
                  ))}
                </div>
              )}
            </div>
          </aside>

          <div className="flex flex-1 flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto bg-muted/30 px-4 py-4 sm:px-6 sm:py-5">
            {/* Report history — mobile dropdown */}
            <div className="mb-4 space-y-2 border-b border-border pb-4 lg:hidden">
              <div className="flex items-center justify-between gap-2">
                <p className="flex items-center gap-1.5 text-label text-foreground">
                  <History className="h-3.5 w-3.5" />
                  Analysis History
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => void runAnalysis(false)}
                  disabled={isBusy}
                >
                  <Plus className="mr-1.5 h-3.5 w-3.5" />
                  New Analysis
                </Button>
              </div>
              {analysisReports.length > 0 && (
                <Select
                  value={selectedReportId ?? undefined}
                  onValueChange={(value) => void handleSelectReport(value, analysisReports)}
                  disabled={isLoadingReportDetail}
                >
                  <SelectTrigger className="h-9 text-body">
                    <SelectValue placeholder="Select a report" />
                  </SelectTrigger>
                  <SelectContent>
                    {analysisReports.map((report) => (
                      <SelectItem key={report.id} value={report.id}>
                        {formatGeneratedAt(report.created_at)}
                        {report.generation_status === 'COMPLETED' && report.content
                          ? ` · ${report.content.overall.score} ${report.content.overall.label}`
                          : ` · ${report.generation_status}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="space-y-5">
              {isLoadingReports && (
                <div className="space-y-4">
                  <Skeleton className="h-40 w-full rounded-2xl" />
                  <Skeleton className="h-24 w-full rounded-2xl" />
                </div>
              )}

              {showEmptyHistoryState && (
                <div className="rounded-2xl border border-dashed border-border bg-background px-6 py-12 text-center">
                  <BarChart3 className="mx-auto h-8 w-8 text-muted-foreground" />
                  <p className="mt-3 text-title-sm text-foreground">
                    No analysis yet for {resumeName}
                  </p>
                  <p className="mx-auto mt-2 max-w-md text-body text-muted-foreground">
                    Run a deep review to score positioning, impact, polish, target fit, and
                    boilerplate risk — and get one-click fixes for the highest-leverage issues.
                  </p>
                  <Button
                    type="button"
                    className="mt-5"
                    onClick={() => void runAnalysis(false)}
                    disabled={isBusy}
                  >
                    <Sparkles className="mr-2 h-4 w-4" />
                    Run your first analysis
                  </Button>
                </div>
              )}

              {!isLoadingReports && !showEmptyHistoryState && (
                <>
                  {analysis && hasUnsavedChanges && !isAnalyzing && (
                    <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-body">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                        <div className="space-y-1">
                          <p className="text-label text-foreground">
                            You have edits that are not reflected in this score yet.
                          </p>
                          <p className="text-muted-foreground">
                            Re-run analysis to score the latest version currently in the editor.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {analysisError && !isAnalyzing && !isShowingApplyAllPreview && (
                    <div className="rounded-xl border border-destructive/40 bg-destructive/10 px-4 py-3 text-body text-destructive">
                      {analysisError}
                    </div>
                  )}

                  {isLoadingReportDetail && !analysis && (
                    <div className="space-y-4">
                      <Skeleton className="h-40 w-full rounded-2xl" />
                      <Skeleton className="h-24 w-full rounded-2xl" />
                    </div>
                  )}

                  {!analysis && isAnalyzing && (
                    <AnalysisLoadingState progress={analyzeProgress} />
                  )}

                  {isGeneratingApplyAllPreview && <ApplyAllLoadingState />}

                  {isShowingApplyAllPreview && applyAllPreviewContent && (
                    <section className="space-y-4">
                      <div className="rounded-2xl border border-border bg-gradient-to-br from-background via-background to-primary/5 p-4 sm:p-6">
                        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                          <div className="space-y-2">
                            <div className="flex flex-wrap items-center gap-2">
                              <Badge variant="outline">Improved draft ready</Badge>
                              <Badge variant="secondary">
                                {applyAllPreviewChanges.length} change
                                {applyAllPreviewChanges.length === 1 ? '' : 's'}
                              </Badge>
                            </div>
                            <p className="text-title-md text-foreground">
                              Review the full before/after comparison
                            </p>
                            <p className="max-w-3xl text-body text-muted-foreground">
                              This draft applies the current analysis recommendations in one coordinated
                              pass. Nothing is saved until you confirm.
                            </p>
                          </div>
                          <div className="rounded-xl border border-border bg-background/80 px-4 py-3 text-body text-muted-foreground">
                            Accepting this draft will save it, close this comparison step, and
                            immediately rerun analysis to show the new score.
                          </div>
                        </div>
                      </div>

                      {applyAllPreviewError && (
                        <div className="rounded-xl border border-destructive/40 bg-destructive/10 px-4 py-3 text-body text-destructive">
                          {applyAllPreviewError}
                        </div>
                      )}

                      {applyAllPreviewChanges.length === 0 ? (
                        <div className="rounded-2xl border border-border bg-background p-5 text-body text-muted-foreground">
                          The improved draft did not produce any material text changes compared with the
                          current resume. You can close this preview or rerun analysis after editing.
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {applyAllPreviewChanges.map((change) => (
                            <div
                              key={change.id}
                              className="rounded-2xl border border-border bg-background p-4 sm:p-5"
                            >
                              <p className="text-title-sm text-foreground">{change.label}</p>
                              <div className="mt-4 grid gap-3 lg:grid-cols-2">
                                <div className="rounded-xl border border-border bg-muted/20 p-4">
                                  <p className="text-overline text-muted-foreground">
                                    Current version
                                  </p>
                                  <p className="mt-2 text-body text-muted-foreground">
                                    {change.before || 'No previous text'}
                                  </p>
                                </div>
                                <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/5 p-4">
                                  <p className="text-overline text-emerald-700">
                                    Improved draft
                                  </p>
                                  <p className="mt-2 text-body text-muted-foreground">
                                    {change.after || 'Removed in improved draft'}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </section>
                  )}

                  {analysis && !isGeneratingApplyAllPreview && !isShowingApplyAllPreview && (
                    <>
                      {isAnalyzing && (
                        <div className="rounded-xl border border-border bg-muted/30 p-4">
                          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <p className="text-title-sm text-foreground">
                              Re-scoring the latest resume version
                            </p>
                            <div className="flex items-center gap-2 text-body text-primary">
                              <Loader2 className="h-4 w-4 animate-spin" />
                              Updating report
                            </div>
                          </div>
                          <div className="mt-3">{analyzeProgress}</div>
                          <p className="mt-3 flex items-start gap-1.5 text-caption text-muted-foreground">
                            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                            You can close this and come back — the analysis runs in the background and your results will be waiting.
                          </p>
                        </div>
                      )}

                      {!isAnalyzing && agentState.status === 'ready' ? analyzeProgress : null}

                      <div className="rounded-xl border border-border bg-background p-4 sm:p-5">
                        <div className="flex flex-wrap items-start gap-4">
                          <ScoreRing score={analysis.overall.score} size="lg" />
                          <div className="min-w-0 flex-1 space-y-1.5">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="text-title-md text-foreground">
                                {analysis.overall.label}
                              </p>
                              <Badge variant="outline">
                                {analysis.mode === 'source-aware'
                                  ? 'Source-aware'
                                  : 'General review'}
                              </Badge>
                              <Badge variant={getRiskVariant(analysis.overall.boilerplateRisk)}>
                                Boilerplate: {analysis.overall.boilerplateRisk}
                              </Badge>
                              {showScoreDelta && (
                                <Badge
                                  variant={
                                    scoreDelta !== null && scoreDelta >= 0
                                      ? 'success'
                                      : 'destructive'
                                  }
                                >
                                  {getDeltaLabel(scoreDelta)}
                                </Badge>
                              )}
                            </div>
                            <p className="text-body text-muted-foreground">
                              {analysis.overall.summary}
                            </p>
                            <p className="text-caption text-muted-foreground">
                              Updated {formatGeneratedAt(analysis.generatedAt)}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="rounded-xl border border-border bg-background">
                        <button
                          type="button"
                          onClick={() => setKeepDoingExpanded((prev) => !prev)}
                          className="flex w-full items-center gap-2 px-4 py-3 text-left"
                        >
                          <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                          <span className="text-label text-foreground">
                            What&apos;s Working
                          </span>
                          <Badge variant="success" className="ml-1">
                            {analysis.keepDoing.length}
                          </Badge>
                          <ChevronDown
                            className={cn(
                              'ml-auto h-4 w-4 text-muted-foreground transition-transform',
                              keepDoingExpanded && 'rotate-180'
                            )}
                          />
                        </button>
                        {keepDoingExpanded && (
                          <div className="border-t border-border px-4 pb-3 pt-2">
                            <ul className="space-y-1.5">
                              {analysis.keepDoing.map((item, index) => (
                                <li
                                  key={`keep-${index}`}
                                  className="flex items-start gap-2 text-body text-muted-foreground"
                                >
                                  <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" />
                                  {item}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>

                      <section className="rounded-xl border border-border bg-background p-4">
                        <div className="flex items-center gap-2 pb-3">
                          <Zap className="h-4 w-4 text-primary" />
                          <h3 className="text-title-sm text-foreground">
                            Highest-Impact Changes
                          </h3>
                        </div>
                        <div className="space-y-1.5">
                          {analysis.topPriorities.map((priority) => (
                            <div
                              key={priority.id}
                              className="flex items-start gap-3 rounded-lg border border-border/60 px-3 py-2.5"
                            >
                              <Badge
                                variant={getPriorityVariant(priority.priority)}
                                className="mt-0.5 shrink-0"
                              >
                                {priority.priority}
                              </Badge>
                              <div className="min-w-0">
                                <p className="text-label text-foreground">
                                  {priority.title}
                                </p>
                                <p className="mt-0.5 text-caption text-muted-foreground">
                                  {priority.reason}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </section>

                      <section className="rounded-xl border border-border bg-background p-4">
                        <div className="flex items-center gap-2 pb-3">
                          <BarChart3 className="h-4 w-4 text-primary" />
                          <h3 className="text-title-sm text-foreground">Section Scores</h3>
                        </div>
                        <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                          {analysis.sections.map((section) => {
                            const isSectionExpanded = expandedSectionKeys.has(section.key);
                            return (
                              <div
                                key={section.key}
                                className="rounded-lg border border-border/60"
                              >
                                <button
                                  type="button"
                                  onClick={() =>
                                    setExpandedSectionKeys((prev) => {
                                      const next = new Set(prev);
                                      if (next.has(section.key)) next.delete(section.key);
                                      else next.add(section.key);
                                      return next;
                                    })
                                  }
                                  className="flex w-full items-center gap-3 px-3 py-2.5 text-left"
                                >
                                  <div className="min-w-0 flex-1">
                                    <div className="flex items-center justify-between gap-2">
                                      <p className="truncate text-label text-foreground">
                                        {section.label}
                                      </p>
                                      <span
                                        className={cn(
                                          'shrink-0 text-label',
                                          section.score >= 75
                                            ? 'text-emerald-600'
                                            : section.score >= 50
                                              ? 'text-amber-600'
                                              : 'text-destructive'
                                        )}
                                      >
                                        {section.score}
                                      </span>
                                    </div>
                                    <div className="mt-1.5 h-1.5 w-full rounded-full bg-muted">
                                      <div
                                        className={cn(
                                          'h-full rounded-full transition-all',
                                          section.score >= 75
                                            ? 'bg-emerald-500'
                                            : section.score >= 50
                                              ? 'bg-amber-500'
                                              : 'bg-destructive'
                                        )}
                                        style={{ width: `${section.score}%` }}
                                      />
                                    </div>
                                    <p className="mt-1 line-clamp-1 text-caption text-muted-foreground">
                                      {section.summary}
                                    </p>
                                  </div>
                                  <ChevronDown
                                    className={cn(
                                      'h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform',
                                      isSectionExpanded && 'rotate-180'
                                    )}
                                  />
                                </button>
                                {isSectionExpanded && (
                                  <div className="space-y-2 border-t border-border px-3 pb-3 pt-2">
                                    {section.strengths.length > 0 && (
                                      <div>
                                        <p className="text-overline text-emerald-700">
                                          Strengths
                                        </p>
                                        <ul className="mt-1 space-y-1">
                                          {section.strengths.map((item, i) => (
                                            <li
                                              key={`${section.key}-s-${i}`}
                                              className="flex items-start gap-1.5 text-caption text-muted-foreground"
                                            >
                                              <CheckCircle2 className="mt-0.5 h-3 w-3 shrink-0 text-emerald-500" />
                                              {item}
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}
                                    {section.concerns.length > 0 && (
                                      <div>
                                        <p className="text-overline text-amber-700">
                                          Concerns
                                        </p>
                                        <ul className="mt-1 space-y-1">
                                          {section.concerns.map((item, i) => (
                                            <li
                                              key={`${section.key}-c-${i}`}
                                              className="flex items-start gap-1.5 text-caption text-muted-foreground"
                                            >
                                              <ShieldAlert className="mt-0.5 h-3 w-3 shrink-0 text-amber-500" />
                                              {item}
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </section>

                      <section className="rounded-xl border border-border bg-background p-4">
                        <div className="flex items-center gap-2 pb-3">
                          <Wand2 className="h-4 w-4 text-primary" />
                          <h3 className="text-title-sm text-foreground">Guided Fixes</h3>
                        </div>
                        <div className="space-y-2">
                          {analysis.suggestions.map((suggestion) => {
                            const isApplying = activeSuggestionId === suggestion.id;
                            return (
                              <div
                                key={suggestion.id}
                                className={cn(
                                  'rounded-lg border border-border bg-background px-3 py-3',
                                  isApplying && 'border-primary/40 bg-primary/5'
                                )}
                              >
                                <div className="flex items-start justify-between gap-3">
                                  <div className="min-w-0 flex-1">
                                    <div className="flex flex-wrap items-center gap-1.5">
                                      <Badge variant={getPriorityVariant(suggestion.priority)}>
                                        {suggestion.priority}
                                      </Badge>
                                      <Badge variant="outline">
                                        {suggestion.targetLabel}
                                      </Badge>
                                      <Badge variant="secondary">
                                        +{suggestion.estimatedScoreGain} pts
                                      </Badge>
                                    </div>
                                    <p className="mt-1.5 text-label text-foreground">
                                      {suggestion.title}
                                    </p>
                                    <p className="mt-0.5 text-caption text-muted-foreground">
                                      {suggestion.reason}
                                    </p>
                                  </div>
                                  <div className="shrink-0">
                                    {suggestion.canAutoApply ? (
                                      <Button
                                        type="button"
                                        size="sm"
                                        onClick={() =>
                                          void handleApplySuggestion(suggestion)
                                        }
                                        disabled={isApplying || isBusy}
                                      >
                                        {isApplying ? (
                                          <>
                                            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                                            Applying
                                          </>
                                        ) : (
                                          <>
                                            <Wand2 className="mr-1.5 h-3.5 w-3.5" />
                                            Apply
                                          </>
                                        )}
                                      </Button>
                                    ) : (
                                      <span className="text-caption text-muted-foreground">
                                        Manual
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </section>
                    </>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Pinned bottom bar inside the right column */}
          <div className="flex items-center gap-2 border-t border-border/60 bg-background px-4 py-2.5 sm:px-6">
          {isShowingApplyAllPreview ? (
            <>
              <Button variant="outline" size="sm" onClick={resetApplyAllPreview} disabled={isApplyingAllPreview}>
                Close Preview
              </Button>
              <Button size="sm" onClick={() => void handleApplyAllPreview()} disabled={isApplyingAllPreview}>
                {isApplyingAllPreview ? (
                  <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />Saving...</>
                ) : (
                  <>Save Improved Draft<ArrowRight className="ml-1.5 h-3.5 w-3.5" /></>
                )}
              </Button>
            </>
          ) : analysis && !isAnalyzing && !isGeneratingApplyAllPreview ? (
            <>
              <Button type="button" size="sm" onClick={() => void handleGenerateApplyAllPreview()} disabled={isBusy || analysis.suggestions.length === 0}>
                <Wand2 className="mr-1.5 h-3.5 w-3.5" />
                Apply all improvements
              </Button>
              <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} disabled={isCloseBlocked} className="ml-auto">
                Close
              </Button>
            </>
          ) : !analysis && !isAnalyzing && !isGeneratingApplyAllPreview && !showEmptyHistoryState ? (
            <>
              <Button variant="outline" size="sm" onClick={() => void runAnalysis(false)} disabled={isBusy}>
                <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
                Try Again
              </Button>
              <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} disabled={isCloseBlocked} className="ml-auto">
                Close
              </Button>
            </>
          ) : (
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} disabled={isCloseBlocked} className="ml-auto">
              Close
            </Button>
          )}
          </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Applying one suggestion rewrites and saves a block immediately, so the
        comparison is the only chance to see what the improvement did. */}
    <ResumeComparisonDialog
      open={appliedSuggestionChanges.length > 0}
      onOpenChange={(nextOpen) => {
        if (nextOpen) return;
        setAppliedSuggestionChanges([]);
        setAppliedSuggestionTitle('');
      }}
      title="Improvement applied"
      description={
        appliedSuggestionTitle
          ? `"${appliedSuggestionTitle}" rewrote this part of your resume.`
          : 'This suggestion rewrote part of your resume.'
      }
      changes={appliedSuggestionChanges}
      closeLabel="Done"
    />
    </>
  );
}
